<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      showTransferModal: false,
      transferSearch: '',
      transferSelectedAgentId: null,
      isTransferring: false,
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
      issues: 'issues/getIssues',
      vipLevelsFromStore: 'vipLevels/getActiveVipLevels',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    currentAccount() {
      const accountId = Number(
        this.currentChat?.account_id || this.currentUser?.account_id
      );
      return (
        (this.currentUser?.accounts || []).find(
          account => Number(account.id) === accountId
        ) || {}
      );
    },
    hasAssignmentOverridePermission() {
      return (this.currentAccount.permissions || []).includes(
        'conversation_assignment_manage'
      );
    },
    isSuperAdminOrAccountAdmin() {
      return (
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentAccount?.role === 'administrator'
      );
    },
    isCurrentAssignee() {
      return (
        Number(this.assignedAgent?.id || 0) ===
        Number(this.currentUser?.id || 0)
      );
    },
    isUnassignedConversation() {
      return !this.assignedAgent?.id;
    },
    canManageAssignee() {
      return (
        this.isSuperAdminOrAccountAdmin ||
        this.hasAssignmentOverridePermission ||
        this.isCurrentAssignee ||
        this.isUnassignedConversation
      );
    },
    transferNoneOption() {
      return {
        id: 0,
        name: this.$t('TEAMS_SETTINGS.LIST.NONE'),
        email: '',
        thumbnail: '',
      };
    },
    transferAgents() {
      return (this.agentsList || []).filter(agent => Number(agent.id) > 0);
    },
    transferSelectableAgents() {
      return [this.transferNoneOption, ...this.transferAgents];
    },
    filteredTransferAgents() {
      const query = (this.transferSearch || '').toLowerCase().trim();
      if (!query) return this.transferSelectableAgents;
      return this.transferSelectableAgents.filter(agent => {
        const agentName = (agent.name || '').toLowerCase();
        const agentEmail = (agent.email || '').toLowerCase();
        return agentName.includes(query) || agentEmail.includes(query);
      });
    },
    transferSelectedAgent() {
      return (
        this.transferSelectableAgents.find(
          agent => Number(agent.id) === Number(this.transferSelectedAgentId)
        ) || null
      );
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    hasAnAssignedIssue() {
      return !!this.currentChat?.issue;
    },
    issuesList() {
      return [{ id: 0, name: 'None' }, ...this.issues];
    },
    vipOptions() {
      if (this.vipLevelsFromStore && this.vipLevelsFromStore.length > 0) {
        return [
          { id: null, name: 'None' },
          ...this.vipLevelsFromStore.map(level => ({
            id: String(level.level),
            name: level.name || `VIP ${level.level}`,
          })),
        ];
      }
      return [
        { id: null, name: 'None' },
        ...Array.from({ length: 50 }, (_, i) => ({
          id: String(i + 1),
          name: `VIP ${i + 1}`,
        })),
      ];
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        if (!this.canManageAssignee) {
          useAlert(this.$t('CONVERSATION.ASSIGNMENT_TRANSFER_FORBIDDEN'));
          return;
        }
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          })
          .catch(error => {
            const errorMessage =
              error?.response?.data?.error ||
              this.$t('CONVERSATION.CHANGE_AGENT_FAILED');
            useAlert(errorMessage);
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedIssue: {
      get() {
        return this.currentChat.issue;
      },
      set(issue) {
        const conversationId = this.currentChat.id;
        const issueId = issue ? issue.id : null;
        this.$store.dispatch('setCurrentChatIssue', { issue, conversationId });
        this.$store
          .dispatch('assignIssue', { conversationId, issueId })
          .then(() => {
            useAlert(
              issue ? `Assigned to issue: ${issue.name}` : 'Issue removed'
            );
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    contact() {
      return this.$store.getters['contacts/getContact'](
        this.currentChat?.meta?.sender?.id
      );
    },
    assignedVip: {
      get() {
        const vipValue = this.contact?.custom_attributes?.vip;
        const selectedOption = this.vipOptions.find(opt => opt.id === vipValue);
        return selectedOption || this.vipOptions[0];
      },
      set(vipItem) {
        const contactId = this.contact?.id;
        if (!contactId) return;

        const vipValue = vipItem ? vipItem.id : null;

        this.$store
          .dispatch('contacts/update', {
            id: contactId,
            custom_attributes: {
              ...this.contact.custom_attributes,
              vip: vipValue,
            },
          })
          .then(() => {
            useAlert('VIP updated successfully');
          });
      },
    },
    showSelfAssign() {
      if (!this.canManageAssignee) {
        return false;
      }
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  methods: {
    onSelfAssign() {
      if (!this.canManageAssignee) {
        useAlert(this.$t('CONVERSATION.ASSIGNMENT_TRANSFER_FORBIDDEN'));
        return;
      }
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (!this.canManageAssignee) {
        useAlert(this.$t('CONVERSATION.ASSIGNMENT_TRANSFER_FORBIDDEN'));
        return;
      }
      if (!selectedItem) {
        this.assignedAgent = null;
        return;
      }
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },
    openTransferModal() {
      if (!this.canManageAssignee) {
        useAlert(this.$t('CONVERSATION.ASSIGNMENT_TRANSFER_FORBIDDEN'));
        return;
      }
      this.transferSearch = '';
      this.transferSelectedAgentId = this.assignedAgent?.id || 0;
      this.showTransferModal = true;
    },
    closeTransferModal() {
      this.showTransferModal = false;
      this.transferSearch = '';
      this.transferSelectedAgentId = null;
      this.isTransferring = false;
    },
    selectTransferAgent(agent) {
      if (agent === null || agent === undefined) return;
      this.transferSelectedAgentId = Number(agent.id);
    },
    confirmTransfer() {
      if (!this.canManageAssignee) {
        useAlert(this.$t('CONVERSATION.ASSIGNMENT_TRANSFER_FORBIDDEN'));
        return;
      }
      if (!this.transferSelectedAgent) return;
      this.isTransferring = true;
      if (Number(this.transferSelectedAgent.id) === 0) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = this.transferSelectedAgent;
      }
      this.closeTransferModal();
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignIssue(selectedIssue) {
      if (selectedIssue.id === 0) {
        this.assignedIssue = null;
      } else if (
        this.assignedIssue &&
        this.assignedIssue.id === selectedIssue.id
      ) {
        this.assignedIssue = null;
      } else {
        this.assignedIssue = selectedIssue;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },
    onClickAssignVip(selectedVipItem) {
      const isSameVip =
        this.assignedVip && this.assignedVip.id === selectedVipItem.id;

      this.assignedVip = isSameVip ? null : selectedVipItem;
    },
  },
};
</script>

<template>
  <div style="background-color: var(--wlab-bg-primary)">
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            color="blue"
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <button
        class="wlab-transfer-trigger"
        type="button"
        :disabled="!canManageAssignee"
        @click="openTransferModal"
      >
        <span class="wlab-transfer-trigger__label">
          {{
            assignedAgent?.name || $t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')
          }}
        </span>
        <span class="i-lucide-chevron-right wlab-transfer-trigger__icon" />
      </button>
      <p v-if="!canManageAssignee" class="wlab-transfer-trigger__hint">
        {{ $t('CONVERSATION.ASSIGNMENT_TRANSFER_FORBIDDEN') }}
      </p>
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNED_ISSUE')"
      />
      <MultiselectDropdown
        :options="issuesList"
        :selected-item="assignedIssue"
        :multiselector-title="$t('CONVERSATION_SIDEBAR.ASSIGN_ISSUE')"
        :multiselector-placeholder="$t('CONVERSATION_SIDEBAR.SELECT_ISSUE')"
        :no-search-result="$t('CONVERSATION_SIDEBAR.NO_ISSUES_FOUND')"
        :input-placeholder="$t('CONVERSATION_SIDEBAR.SEARCH_ISSUES')"
        @select="onClickAssignIssue"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.VIP_LEVEL')"
      />
      <MultiselectDropdown
        :options="vipOptions"
        :selected-item="assignedVip"
        :multiselector-title="$t('CONVERSATION_SIDEBAR.VIP_LEVEL')"
        :multiselector-placeholder="$t('CONVERSATION_SIDEBAR.SELECT_VIP_LEVEL')"
        :no-search-result="$t('CONVERSATION_SIDEBAR.NO_VIP_LEVELS_FOUND')"
        :input-placeholder="$t('CONVERSATION_SIDEBAR.SEARCH_VIP_LEVEL')"
        @select="onClickAssignVip"
      />
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />

    <Teleport to="body">
      <div
        v-if="showTransferModal"
        data-testid="lc-modal-overlay"
        class="lc-Modal-module__modal-base__overlay___BXG-c lc-Modal-module__modal-base__overlay--visible___SkPQ3"
        style="position: fixed; overflow: auto; inset: 0px"
        @click.self="closeTransferModal"
      >
        <div
          role="dialog"
          aria-modal="true"
          class="lc-Modal-module__modal-base___W3hsn wlab-transfer-modal"
          data-testid="chat-transfer-modal"
        >
          <div class="lc-Modal-module__modal__header___22qVU">
            <div class="lc-Modal-module__modal__heading___TQl-G">
              Transfer chat to...
            </div>
            <button
              class="wlab-transfer-modal__close"
              aria-label="Close modal"
              type="button"
              @click="closeTransferModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <div
            data-testid="modal-body"
            class="lc-Modal-module__modal__body___K3X3J"
          >
            <div class="wlab-transfer-modal__tabs">
              <div role="tablist" class="wlab-transfer-modal__tabs-list">
                <button
                  class="wlab-transfer-modal__tab wlab-transfer-modal__tab--selected"
                  data-testid="tab-to-select-agent"
                  aria-selected="true"
                  type="button"
                >
                  Agent
                  <span class="wlab-transfer-modal__tab-count">
                    ({{ transferAgents.length }})
                  </span>
                </button>
              </div>
              <div class="wlab-transfer-modal__search-wrap">
                <span
                  class="i-lucide-search size-4 wlab-transfer-modal__search-icon"
                />
                <input
                  v-model.trim="transferSearch"
                  role="searchbox"
                  type="text"
                  placeholder="Search ..."
                  class="wlab-transfer-modal__search-input"
                />
              </div>
            </div>

            <div class="wlab-transfer-modal__list-wrap">
              <div
                class="wlab-transfer-modal__list"
                data-testid="agent-transfer-list"
              >
                <button
                  v-for="agent in filteredTransferAgents"
                  :key="agent.id"
                  type="button"
                  class="wlab-transfer-modal__list-item"
                  :class="{
                    'wlab-transfer-modal__list-item--active':
                      Number(transferSelectedAgentId) === Number(agent.id),
                  }"
                  data-testid="agent-transfer-list-item"
                  @click="selectTransferAgent(agent)"
                >
                  <div class="wlab-transfer-modal__avatar">
                    <img
                      v-if="agent.thumbnail"
                      :src="agent.thumbnail"
                      :alt="agent.name"
                      class="wlab-transfer-modal__avatar-image"
                    />
                    <span v-else class="wlab-transfer-modal__avatar-fallback">
                      {{ (agent.name || '?').slice(0, 1).toUpperCase() }}
                    </span>
                  </div>
                  <div class="wlab-transfer-modal__meta">
                    <span
                      class="wlab-transfer-modal__name"
                      data-testid="agent-name"
                    >
                      {{ agent.name }}
                    </span>
                    <span
                      class="wlab-transfer-modal__sub"
                      data-testid="no-active-chats"
                    >
                      {{ agent.email || 'no active chats' }}
                    </span>
                  </div>
                </button>
                <div
                  v-if="filteredTransferAgents.length === 0"
                  class="wlab-transfer-modal__empty"
                >
                  {{ $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT') }}
                </div>
              </div>
            </div>
          </div>

          <div class="lc-Modal-module__modal__footer___-z-ze">
            <div class="wlab-transfer-modal__footer-left">
              Edit internal note
            </div>
            <button
              class="wlab-transfer-modal__transfer-btn"
              type="button"
              data-testid="transfer-button"
              :disabled="!transferSelectedAgent || isTransferring"
              @click="confirmTransfer"
            >
              {{ isTransferring ? 'Transferring...' : 'Transfer' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.multiselect-wrap--small {
  padding-top: 5px;
}

.wlab-transfer-trigger {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--wlab-border-default, #e2e8f0);
  background: var(--wlab-bg-primary, #ffffff);
  color: var(--wlab-text-primary, #0f172a);
  padding: 0 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.wlab-transfer-trigger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.wlab-transfer-trigger__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wlab-transfer-trigger__icon {
  color: var(--wlab-text-secondary, #64748b);
}

.wlab-transfer-trigger__hint {
  margin-top: 6px;
  font-size: 12px;
  color: #b45309;
}

.lc-Modal-module__modal-base__overlay___BXG-c {
  z-index: 10000;
  background: rgba(15, 23, 42, 0.45);
}

.lc-Modal-module__modal-base__overlay--visible___SkPQ3 {
  display: flex;
}

.wlab-transfer-modal {
  display: flex;
  flex-direction: column;
  width: 640px;
  min-height: 505px;
  max-width: calc(100vw - 24px);
  margin: auto;
  border-radius: 14px;
  background: #ffffff;
  color: #131317;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 22px 28px 16px;
}

.lc-Modal-module__modal__header___22qVU {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
}

.lc-Modal-module__modal__heading___TQl-G {
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
}

.wlab-transfer-modal__close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wlab-transfer-modal__close:hover {
  background: rgba(15, 23, 42, 0.06);
}

.lc-Modal-module__modal__body___K3X3J {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.wlab-transfer-modal__tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.wlab-transfer-modal__tabs-list {
  display: inline-flex;
  align-items: center;
}

.wlab-transfer-modal__tab {
  border: none;
  background: transparent;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #475467;
  cursor: pointer;
}

.wlab-transfer-modal__tab--selected {
  color: #2563eb;
}

.wlab-transfer-modal__tab-count {
  margin-left: 4px;
}

.wlab-transfer-modal__search-wrap {
  width: 228px;
  height: 34px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  padding: 0 8px;
  gap: 6px;
}

.wlab-transfer-modal__search-icon {
  color: #667085;
}

.wlab-transfer-modal__search-input {
  border: none;
  outline: none;
  width: 100%;
  background: transparent;
  font-size: 13px;
  color: #111827;
}

.wlab-transfer-modal__list-wrap {
  border: 1px solid #eaecf0;
  border-radius: 12px;
  overflow: hidden;
  flex: 1;
  min-height: 280px;
}

.wlab-transfer-modal__list {
  max-height: 345px;
  overflow-y: auto;
  padding: 8px;
}

.wlab-transfer-modal__list-item {
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  cursor: pointer;
}

.wlab-transfer-modal__list-item:hover {
  background: rgba(15, 23, 42, 0.04);
}

.wlab-transfer-modal__list-item--active {
  background: rgba(37, 99, 235, 0.12);
}

.wlab-transfer-modal__avatar {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  overflow: hidden;
  background: #e5e7eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wlab-transfer-modal__avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wlab-transfer-modal__avatar-fallback {
  font-size: 13px;
  font-weight: 700;
  color: #475467;
}

.wlab-transfer-modal__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.wlab-transfer-modal__name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wlab-transfer-modal__sub {
  font-size: 12px;
  color: #6b7280;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wlab-transfer-modal__empty {
  padding: 14px 10px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}

.lc-Modal-module__modal__footer___-z-ze {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #eaecf0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.wlab-transfer-modal__footer-left {
  font-size: 13px;
  color: #667085;
}

.wlab-transfer-modal__transfer-btn {
  border: none;
  border-radius: 8px;
  background: #2563eb;
  color: #ffffff;
  min-width: 96px;
  height: 36px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.wlab-transfer-modal__transfer-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
